import surveys from "../services/surveys";
import notification from "../services/notification";
import rewards from "../services/rewards";

import auth from "./auth";

const state = {
    isLoading: false,
    isUcoin: false,
    errors: null,
    surveys: [],
    survey: null,
    surveyLogo: null,
    surveyBanner: null,
    surveyImage: null,
    surveyImageThankYou: null,
    surveyLink: null,
    pageDetails: null,
    questionProperties: {}, // used for confirm save question and delete question element
    designDetails: null,
    questionSortCount: 0,
    surveyExportFile: null,
    surveyUuid: null,
    import: false,
    importfile: null,
    importBase: null,
    rewardsList: []
};

const getters = {
    totalQuestions(state) {
        return state.survey && Object.keys(state.survey).includes("pages")
            ? state.survey.pages.reduce(
                  (page, { questions }) => page + questions.length,
                  0
              )
            : 0;
    },
    isLoading: state => state.isLoading,
    isUcoin: state => state.isUcoin,
    errors: state => state.errors,
    survey: state => state.survey,
    surveyLink: state => state.surveyLink,
    questionProperties: state => state.questionProperties,
    questionSortCount: state => state.questionSortCount,
    isReadOnly: state => (state.survey ? state.survey.respondents > 0 : false),
    pageDetails: state => state.pageDetails,
    rewardsList: state => state.rewardsList
};

const mutations = {
    setIsLoading(state, payload) {
        state.isLoading = payload;
    },
    setIsUcoin(state, payload) {
        state.isUcoin = payload;
    },
    setErrors(state, payload) {
        state.errors = payload;
    },
    setSurveys(state, payload) {
        state.surveys = payload;
    },
    setSurvey(state, payload) {
        state.survey = payload;
    },
    setSurveyLogo(state, payload) {
        state.surveyLogo = payload;
    },
    setSurveyBanner(state, payload) {
        state.surveyBanner = payload;
    },
    setSurveyImage(state, payload) {
        state.surveyImage = payload;
    },
    setSurveyImageThankYou(state, payload) {
        state.surveyImageThankYou = payload;
    },
    setSurveyLink(state, link) {
        state.surveyLink = link;
    },
    setPageDetails(state, payload) {
        state.pageDetails = payload;
    },
    setQuestionProperties(state, payload) {
        state.questionProperties = payload;
    },
    setDesignDetails(state, payload) {
        state.designDetails = payload;
    },
    setQuestionSortCount(state, payload) {
        state.questionSortCount = payload;
    },
    setRewardsList(state, payload) {
        state.rewardsList = payload;
    },
    setStringifyContent(state, payload) {
        const question = state.survey.pages[payload.page_index].questions.find(
            question => question.uuid === payload.question_uuid
        );
        if (
            payload.question_type === "multiple_choice" ||
            payload.question_type === "checkbox" ||
            payload.question_type === "dropdown"
        ) {
            const defaultMultipleChoice = {
                visible: question.contents.visible,
                required: question.contents.required,
                attributes: {
                    width: question.contents.width,
                    options: question.contents.elements.map(answer => {
                        return {
                            answer: answer
                        };
                    }),
                    gigya_answer_ids: question.contents.gigya_answer_ids.map(
                        gigya_answer_id => {
                            return {
                                gigya_answer_id: gigya_answer_id
                            };
                        }
                    ),
                    images: question.contents.images.map(image => ({
                        ...image
                    }))
                }
            };

            question["content"] = JSON.stringify([defaultMultipleChoice]);
        } else if (payload.question_type === "contact_details") {
            question["content"] = JSON.stringify(
                question.contents.elements
                    .map(element => {
                        const field = {
                            visible: element.visible,
                            required: element.required,
                            attributes: {
                                label:
                                    element.label !== null
                                        ? element.label
                                        : element.type,
                                placeholder: element.placeholder,
                                type: element.type
                            },
                            gigya_answer_id: element.gigya_answer_id
                        };
                        if (Object.keys(element).includes("options"))
                            field["options"] = element.options;
                        return field;
                    })
                    .flat()
            );
        } else if (payload.question_type === "open_ended") {
            question["content"] = JSON.stringify([
                {
                    visible: question.contents.visible,
                    required: question.contents.required,
                    attributes: {
                        char_length: question.contents.char_length,
                        placeholder: question.contents.placeholder
                    }
                }
            ]);
        } else if (payload.question_type === "file_upload") {
            question["content"] = JSON.stringify([
                {
                    visible: question.contents.visible,
                    required: question.contents.required,
                    attributes: {
                        size_limit: question.contents.size_limit,
                        allowed_file_types: question.contents.allowed_file_types
                    }
                }
            ]);
        } else if (payload.question_type === "rating") {
            question["content"] = JSON.stringify([
                {
                    visible: question.contents.visible,
                    required: question.contents.required,
                    attributes: {
                        icon: question.contents.icon,
                        options: question.contents.elements.map(
                            (rating, index) => {
                                return {
                                    value: index + 1,
                                    label: rating.label
                                };
                            }
                        ),
                        gigya_answer_ids: question.contents.gigya_answer_ids.map(
                            gigya_answer_id => {
                                return {
                                    gigya_answer_id:
                                        gigya_answer_id.gigya_answer_id
                                };
                            }
                        )
                    }
                }
            ]);
        }
    },
    cancelQuestion(state, payload) {
        const question = payload.questions.find(
            question => question.uuid === payload.question_uuid
        );

        state.survey.pages
            .find(page => page.uuid === payload.page_uuid)
            .questions.find(
                question => question.uuid === payload.question_uuid
            ).question =
            question.question;
        state.survey.pages
            .find(page => page.uuid === payload.page_uuid)
            .questions.find(
                question => question.uuid === payload.question_uuid
            ).is_enabled =
            question.is_enabled;

        state.survey.pages
            .find(page => page.uuid === payload.page_uuid)
            .questions.find(
                question => question.uuid === payload.question_uuid
            )["content"] =
            question.content;
    },
    setSurveyExportFile(state, payload) {
        state.surveyExportFile = payload;
    },
    setSurveyUuid(state, surveyUuid) {
        state.surveyUuid = surveyUuid;
    },
    setImportFile(state, importfile) {
        state.importfile = importfile;
    },
    setImportBase(state, importBase) {
        console.log(importBase);
        state.importBase = importBase;
        state.import = true;
    },
    setImportBaseDefault(state) {
        state.surveyUuid = null;
        state.importBase = null;
        state.import = false;
    }
};

const actions = {
    getSurveys: async ({ commit }, payload) => {
        try {
            commit("setIsLoading", true);

            const { data } = await surveys.getSurveys(payload);

            commit("setSurveys", data);
        } catch (errors) {
            commit("setErrors", errors);
        } finally {
            commit("setIsLoading", false);
        }
    },
    getSurvey: async ({ commit }, uuid) => {
        commit("setIsLoading", true);
        try {
            commit("setSurvey", null);
            const response = await surveys.getSurvey(uuid);
            commit("setSurvey", response.data);
        } catch (errors) {
            commit("setErrors", errors.errors);
            notification.warning("Oops, cannot get survey.");
        } finally {
            commit("setIsLoading", false);
        }
    },
    createSurvey: async ({ commit }, payload) => {
        commit("setIsLoading", true);
        try {
            const response = await surveys.createSurvey(payload);
            commit("setSurvey", response.data);
            notification.success(response.message);
            commit("setIsLoading", false);
        } catch (errors) {
            commit("setErrors", errors.errors);
            notification.warning("Oops, cannot create survey.");
            commit("setIsLoading", false);
        }
    },
    deleteSurvey: async ({ commit }, surveyId) => {
        commit("setIsLoading", true);
        try {
            const { message } = await surveys.deleteSurvey(surveyId);
            notification.success(message);
            commit("setIsLoading", false);
        } catch (errors) {
            commit("setErrors", errors.errors);
            notification.warning("Oops, cannot delete survey.");
            commit("setIsLoading", false);
        }
    },
    deletePage: async ({ commit, dispatch }, payload) => {
        commit("setIsLoading", true);

        try {
            const { pageId, surveyId } = payload;
            const { message } = await surveys.deletePage({ uuid: pageId });

            dispatch("getSurvey", surveyId);
            notification.success(message);
        } catch (errors) {
            commit("setErrors", errors);
            notification.warning("Oops, cannot delete page.");
        } finally {
            commit("setIsLoading", false);
        }
    },
    createQuestion: async ({ commit }, payload) => {
        commit("setIsLoading", true);
        try {
            const response = await surveys.createQuestion(payload);
            commit("setErrors", null);
            notification.success(response.message);
        } catch (errors) {
            commit("setErrors", errors.errors);
            notification.warning("Oops, cannot create question.");
        } finally {
            commit("setIsLoading", false);
        }
    },
    updateQuestion: async ({ commit }, payload) => {
        commit("setIsLoading", true);
        try {
            const response = await surveys.updateQuestion(payload);
            commit("setErrors", null);
            notification.success(response.message);
        } catch (errors) {
            commit("setErrors", errors.errors);
            notification.warning("Oops, cannot update question.");
        } finally {
            commit("setIsLoading", false);
        }
    },
    deleteQuestion: async ({ commit }, uuid) => {
        commit("setIsLoading", true);
        try {
            const response = await surveys.deleteQuestion(uuid);
            notification.success(response.message);
        } catch (errors) {
            commit("setErrors", errors.errors);
            notification.warning("Oops, cannot delete question.");
        } finally {
            commit("setIsLoading", false);
        }
    },
    createDuplicateQuestion: ({ state, commit }, payload) => {
        const survey = state.survey;
        const { pageIndex, questionIndex } = payload;
        const newId =
            survey.pages[pageIndex].questions.reduce(
                (previous, current) =>
                    previous.id > current.id ? previous : current
            ).id + 1;

        survey.pages[pageIndex].questions.splice(questionIndex + 1, 0, {
            ...survey.pages[pageIndex].questions[questionIndex],
            id: newId,
            uuid: null
        });

        commit("setSurvey", survey);
    },
    getQuestions: async ({ commit }, payload) => {
        try {
            const { data } = await surveys.getQuestions(payload.page_uuid);
            commit("cancelQuestion", {
                page_uuid: payload.page_uuid,
                question_uuid: payload.question_uuid,
                questions: data.questions
            });
            commit("setErrors", null);
        } catch (errors) {
            commit("setErrors", errors.errors);
        }
    },
    updateQuestionSort: async ({ state, commit }, payload) => {
        commit("setIsLoading", true);
        try {
            const response = await surveys.updateQuestionSort(payload);
            commit("setQuestionSortCount", (state.questionSortCount += 1));
            if (state.questionSortCount === 1)
                notification.success(response.message);
        } catch (errors) {
            notification.warning("Oops, cannot sort questions.");
            commit("setErrors", errors.errors);
        } finally {
            commit("setIsLoading", false);
        }
    },
    createPage: async ({ commit, dispatch }, payload) => {
        commit("setIsLoading", true);

        try {
            const {
                pageName,
                hidePreviousButton,
                surveyId,
                surveyUuid
            } = payload;
            const { message } = await surveys.createPage({
                page_name: pageName,
                hide_prev_button: hidePreviousButton,
                survey_id: surveyId
            });

            dispatch("getSurvey", surveyUuid);
            notification.success(message);
        } catch (errors) {
            commit("setErrors", errors);
            notification.warning("Oops, cannot create page.");
        } finally {
            commit("setIsLoading", false);
        }
    },
    deleteQuestionElement: ({ state, commit }, payload) => {
        const { pageId, questionId, elementId } = payload;
        const survey = state.survey;
        const pageIndex = survey.pages.findIndex(page => page.id === pageId);
        const questionIndex = survey.pages[pageIndex].questions.findIndex(
            question => question.id === questionId
        );

        survey.pages[pageIndex].questions[
            questionIndex
        ].contents.elements = survey.pages[pageIndex].questions[
            questionIndex
        ].contents.elements.filter(({ id }) => id !== elementId);

        commit("setSurvey", survey);
    },
    getSurveyUrl: async ({ commit }, uuid) => {
        commit("setIsLoading", true);
        try {
            const response = await surveys.getSurveyUrl(uuid);
            commit("setSurveyLink", response.data.url);
            commit("setIsLoading", false);
        } catch (errors) {
            commit("setErrors", errors.errors);
            commit("setIsLoading", false);
        }
    },
    updateSurvey: async ({ commit }, payload) => {
        commit("setIsLoading", true);
        try {
            const response = await surveys.updateSurvey(payload);
            notification.success(response.message);
            commit("setIsLoading", false);
        } catch (errors) {
            commit("setErrors", errors.errors);
            notification.warning("Oops, cannot update survey.");
            commit("setIsLoading", false);
        }
    },
    duplicateSurvey: async ({ commit }, payload) => {
        commit("setIsLoading", true);
        try {
            const response = await surveys.duplicateSurvey(payload);
            notification.success(response.message);
            commit("setIsLoading", false);
        } catch (errors) {
            commit("setErrors", errors.errors);
            notification.warning("Oops, cannot duplicate survey.");
            commit("setIsLoading", false);
        }
    },
    updatePage: async ({ commit, dispatch }, payload) => {
        commit("setIsLoading", true);

        try {
            const {
                pageName,
                hidePreviousButton,
                pageId,
                surveyId,
                analytics_code
            } = payload;

            const params = {
                page_name: pageName,
                hide_prev_button: hidePreviousButton,
                uuid: pageId
            };

            if (analytics_code) params["analytics_code"] = analytics_code;

            const { message } = await surveys.updatePage(params);

            dispatch("getSurvey", surveyId);
            notification.success(message);
        } catch (errors) {
            commit("setErrors", errors);
            notification.warning("Oops, cannot update page.");
        } finally {
            commit("setIsLoading", false);
        }
    },
    getPageDetails: async ({ commit }, uuid) => {
        commit("setIsLoading", true);

        try {
            const { data } = await surveys.getPageDetails(uuid);

            if (Object.keys(data).length) commit("setPageDetails", data);
            else {
                commit("setPageDetails", null);
                notification.warning("Oops, cannot get page details.");
            }
        } catch (errors) {
            commit("setErrors", errors);
            notification.warning("Oops, cannot get page details.");
        } finally {
            commit("setIsLoading", false);
        }
    },
    saveDesign: async ({ commit }, payload) => {
        commit("setIsLoading", true);
        try {
            const { message } = await surveys.saveDesign(payload);
            notification.success(message);
            commit("setIsLoading", false);
        } catch (errors) {
            commit("setErrors", errors.errors);
            notification.warning("Oops, cannot save design.");
            commit("setIsLoading", false);
        }
    },
    updatePageSort: async ({ commit }, payload) => {
        commit("setIsLoading", true);

        try {
            const response = await surveys.updatePageSort(payload);
            notification.success(response.message);
        } catch (errors) {
            commit("setErrors", errors.errors);
            notification.warning("Oops, cannot update page sort.");
        } finally {
            commit("setIsLoading", false);
        }
    },
    getDesignDetails: async ({ commit }, uuid) => {
        commit("setIsLoading", true);
        try {
            commit("setDesignDetails", null);
            const response = await surveys.getDesignDetails(uuid);
            commit("setDesignDetails", response.data);
        } catch (error) {
            commit("setErrors", error);
            notification.warning("Oops, cannot get design details.");
        } finally {
            commit("setIsLoading", false);
        }
    },
    getSurveyExportFile: async ({ commit }, uuid) => {
        commit("setIsLoading", true);
        commit("setSurveyExportFile", null);

        try {
            let response = await surveys.getSurveyExportFile(uuid);
            notification.success(
                "Your data is being exported. You will receive it via email."
            );
            // commit("setSurveyExportFile", data.url);
        } catch (errors) {
            notification.warning(errors.errors.message);
        } finally {
            commit("setIsLoading", false);
        }
    },
    readImport({ commit, state, dispatch }) {
        commit("setIsLoading", true);

        let reader = new FileReader();
        reader.readAsDataURL(state.importfile);

        reader.onload = e => {
            commit("setImportBase", e.target.result.split("base64,").pop());
        };
    },
    importSurvey: async ({ commit, state }) => {
        try {
            // console.log(state.surveyUuid);
            // console.log(state.importBase);
            let response = await surveys.import(state.surveyUuid, {
                file: state.importBase
            });

            commit("setImportBaseDefault");
            notification.success(response.message);
        } catch (errors) {
            notification.warning(errors.errors.message);
        } finally {
            commit("setIsLoading", false);
        }
    },
    getRewardsList: async ({ commit }) => {
        try {
            const response = await rewards.getRewardsList();
            const mappedData = response.map(({ uuid, name, description }) => ({
                name: name,
                value: uuid,
                description: description
            }));
            commit("setRewardsList", mappedData);
        } catch (errors) {
            notification.warning(errors);
        }
    },
    isUcoin({ commit }) {
        const ucoinMerchantId =
            process.env.VUE_APP_MODE == "Production"
                ? "444e8eee-9861-4629-85a5-ad2aef39cecb"
                : "82b1369e-b498-4876-868f-17fed2f16355";
        let x = auth.state.user.merchant_uuid == ucoinMerchantId ? true : false;
        commit("setIsUcoin", x);
        return x;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
